/** Uses the video's text-track clock, including pause, seek and playback end. */
export class CaptionDirector {
  private readonly track = document.createElement('track');
  private enabled = false;
  private immersive = false;
  private loaded = false;

  constructor(private readonly video: HTMLVideoElement,
    private readonly button: HTMLButtonElement,
    private readonly overlay: HTMLElement,
    private readonly text: HTMLElement,
    private readonly root: HTMLElement,
    private readonly reportError: (message: string) => void) {
    button.disabled = true;
    button.addEventListener('click', this.toggle);
    this.track.addEventListener('load', this.onLoad);
    this.track.addEventListener('error', this.onError);
    this.track.track.addEventListener('cuechange', this.render);
    video.addEventListener('seeking', this.render);
    video.addEventListener('timeupdate', this.render);
  }

  load(path: string): void {
    this.track.kind = 'captions';
    this.track.label = 'English — review draft';
    this.track.srclang = 'en';
    this.track.src = `${import.meta.env.BASE_URL}${path}`;
    this.video.append(this.track);
    // Hidden lets the native parser and media clock run without native rendering.
    this.track.track.mode = 'hidden';
  }

  setImmersive(value: boolean): void { this.immersive = value; this.render(); }

  readonly toggle = (): void => {
    if (!this.loaded) return;
    this.enabled = !this.enabled;
    this.button.setAttribute('aria-pressed', String(this.enabled));
    this.button.setAttribute('aria-label', this.enabled ? 'Hide draft captions' : 'Show draft captions');
    this.button.title = this.enabled ? 'Hide draft captions' : 'Show draft captions';
    this.root.classList.toggle('captions-enabled', this.enabled);
    this.render();
  };

  private readonly onLoad = (): void => {
    this.loaded = true;
    this.button.disabled = false;
    this.render();
  };
  private readonly onError = (): void => {
    this.loaded = false;
    this.button.disabled = true;
    this.enabled = false;
    this.root.classList.remove('captions-enabled');
    this.button.setAttribute('aria-pressed', 'false');
    this.render();
    this.reportError('Captions could not load. The reflection guide is still available.');
  };
  private readonly render = (): void => {
    const time = this.video.currentTime;
    // Filter explicitly during seeking so old activeCues cannot flash on screen.
    const cues = [...(this.track.track.activeCues ?? [])].filter(c => c.startTime <= time && time < c.endTime);
    const value = cues.map(c => c instanceof VTTCue ? c.getCueAsHTML().textContent ?? '' : '').join('\n');
    if (this.text.textContent !== value) this.text.textContent = value;
    this.overlay.hidden = !this.enabled || !this.immersive || !value;
  };

  dispose(): void {
    this.button.removeEventListener('click', this.toggle);
    this.track.removeEventListener('load', this.onLoad);
    this.track.removeEventListener('error', this.onError);
    this.track.track.removeEventListener('cuechange', this.render);
    this.video.removeEventListener('seeking', this.render);
    this.video.removeEventListener('timeupdate', this.render);
    this.track.remove();
  }
}
